/**
 * DepotResultsSection - Display depot cost analysis results
 *
 * Shows cost summary cards, monthly breakdown table from stored
 * depot_config.cost_analysis data.
 */

import React from 'react';
import {
    Alert,
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@mui/material';
import { Assessment as ResultsIcon } from '@mui/icons-material';
import { SectionCard, vortxColors } from '@vortx/shared-ui';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function StatCard({ label, value, unit }) {
    return (
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                >
                    {label}
                </Typography>
                <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, color: vortxColors.teal, mt: 0.5 }}
                >
                    {value}
                </Typography>
                {unit && (
                    <Typography variant="caption" color="text.secondary">
                        {unit}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
}

function formatCurrency(val) {
    if (val == null) return '\u2014';
    return `$${Number(val).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatNumber(val, decimals = 0) {
    if (val == null) return '\u2014';
    return Number(val).toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
}

function DepotResultsSection({ project }) {
    const depotConfig = project?.depot_config;
    const result = depotConfig?.cost_analysis;

    if (!result) {
        return (
            <SectionCard title="Cost Analysis Results" icon={<ResultsIcon />}>
                <Alert severity="info">
                    No cost analysis results yet. Go to the Upload tab to upload a load profile and run the analysis.
                </Alert>
            </SectionCard>
        );
    }

    const monthly = result.monthly_summary || [];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Summary Cards */}
            <SectionCard title="Cost Analysis Results" icon={<ResultsIcon />}>
                <Grid container spacing={2}>
                    <Grid item xs={6} sm={4} md={2.4}>
                        <StatCard label="Annual Cost" value={formatCurrency(result.annual_total_cost)} />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2.4}>
                        <StatCard label="Energy Cost" value={formatCurrency(result.annual_energy_cost)} />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2.4}>
                        <StatCard label="Demand Charges" value={formatCurrency(result.annual_demand_charges)} />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2.4}>
                        <StatCard label="Peak Demand" value={formatNumber(result.peak_kw, 1)} unit="kW" />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2.4}>
                        <StatCard
                            label="Load Factor"
                            value={formatNumber((result.load_factor || 0) * 100, 1)}
                            unit="%"
                        />
                    </Grid>
                </Grid>

                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Total annual consumption: {formatNumber(result.annual_energy_kwh)} kWh |
                        Average daily: {formatNumber(result.avg_daily_kwh, 1)} kWh
                    </Typography>
                </Box>
            </SectionCard>

            {/* Monthly Breakdown Table */}
            {monthly.length > 0 && (
                <SectionCard title="Monthly Breakdown">
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Month</TableCell>
                                    <TableCell align="right">Energy (kWh)</TableCell>
                                    <TableCell align="right">Peak (kW)</TableCell>
                                    <TableCell align="right">Energy Cost</TableCell>
                                    <TableCell align="right">Demand Charge</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {monthly.map((m) => (
                                    <TableRow key={m.month}>
                                        <TableCell>{MONTHS[m.month - 1] || m.month}</TableCell>
                                        <TableCell align="right">{formatNumber(m.energy_kwh)}</TableCell>
                                        <TableCell align="right">{formatNumber(m.peak_kw, 1)}</TableCell>
                                        <TableCell align="right">{formatCurrency(m.energy_cost)}</TableCell>
                                        <TableCell align="right">{formatCurrency(m.demand_charge)}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                                            {formatCurrency(m.total_cost)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </SectionCard>
            )}
        </Box>
    );
}

export default DepotResultsSection;
